/**
 * ShareOntologyModal — Share an ontology with another user by email (read-only).
 * Also displays current shares with ability to revoke.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TextInput,
  InlineNotification,
  Button,
  Loading,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  Tag,
  Toggle,
  RadioButtonGroup,
  RadioButton,
} from '@carbon/react';
import { TrashCan, Share } from '@carbon/icons-react';
import { shareApi, type OntologyShareInfo } from '@/lib/api/share.api';
import { ontologyApi } from '@/lib/api/ontology.api';

interface ShareOntologyModalProps {
  open: boolean;
  onClose: () => void;
  ontologyId: string;
  ontologyName: string;
}

export function ShareOntologyModal({
  open,
  onClose,
  ontologyId,
  ontologyName,
}: ShareOntologyModalProps) {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<'read' | 'write'>('read');
  const [shares, setShares] = useState<OntologyShareInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // A13: public/private visibility.
  const [isPublic, setIsPublic] = useState(false);
  const [visBusy, setVisBusy] = useState(false);

  // Load existing shares when modal opens
  const loadShares = useCallback(async () => {
    if (!ontologyId) return;
    setSharesLoading(true);
    try {
      const data = await shareApi.getShares(ontologyId);
      setShares(data);
    } catch (err) {
      // Silently fail — shares list is not critical
      console.error('Failed to load shares', err);
    } finally {
      setSharesLoading(false);
    }
  }, [ontologyId]);

  useEffect(() => {
    if (open) {
      loadShares();
      setEmail('');
      setError(null);
      setSuccess(null);
      // Load current visibility so the toggle reflects the real state.
      ontologyApi
        .getById(ontologyId)
        .then((o) => setIsPublic(o.visibility === 'public'))
        .catch(() => {});
    }
  }, [open, loadShares, ontologyId]);

  const handleToggleVisibility = async (next: boolean) => {
    setVisBusy(true);
    setError(null);
    const previous = isPublic;
    setIsPublic(next); // optimistic
    try {
      await ontologyApi.updateOntology(ontologyId, { visibility: next ? 'public' : 'private' });
      setSuccess(next ? 'Ontology is now public (any signed-in user can view it).' : 'Ontology is now private.');
    } catch (err: any) {
      setIsPublic(previous); // revert
      setError(err?.message || 'Failed to update visibility.');
    } finally {
      setVisBusy(false);
    }
  };

  const handleShare = async () => {
    if (!email.trim()) {
      setError('Please enter an email address.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await shareApi.shareOntology(ontologyId, email.trim(), permission);
      setSuccess(
        `Ontology shared with ${email.trim()} (${permission === 'write' ? 'can edit' : 'read-only'})`
      );
      setEmail('');
      // Refresh shares list
      await loadShares();
    } catch (err: any) {
      const message =
        err?.message || 'Unable to share. Please verify the email address.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (shareId: string, shareEmail: string) => {
    try {
      await shareApi.revokeShare(ontologyId, shareId);
      setShares((prev) => prev.filter((s) => s.id !== shareId));
      setSuccess(`Access revoked for ${shareEmail}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke access.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleShare();
    }
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="sm">
      <ModalHeader
        title={`Share "${ontologyName}"`}
        label="Read-only or edit access"
      />
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Public / private visibility (A13) */}
          <Toggle
            id="ontology-visibility-toggle"
            data-testid="visibility-toggle"
            size="sm"
            labelText="Public"
            labelA="Private (only you + people you share with)"
            labelB="Public (any signed-in user can view)"
            toggled={isPublic}
            disabled={visBusy}
            onToggle={handleToggleVisibility}
          />

          {/* Email input + Share button */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <TextInput
              id="share-email-input"
              labelText="User email"
              placeholder="Enter the user's email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
                setSuccess(null);
              }}
              onKeyDown={handleKeyDown}
              disabled={loading}
              style={{ flex: 1 }}
            />
            <Button
              kind="primary"
              size="md"
              renderIcon={Share}
              onClick={handleShare}
              disabled={loading || !email.trim()}
            >
              {loading ? 'Sharing...' : 'Share'}
            </Button>
          </div>

          {/* A13: read-only vs write access for the invited user */}
          <RadioButtonGroup
            legendText="Access level"
            name="share-permission"
            valueSelected={permission}
            onChange={(v) => setPermission(v as 'read' | 'write')}
          >
            <RadioButton labelText="Read-only" value="read" id="share-perm-read" />
            <RadioButton labelText="Can edit" value="write" id="share-perm-write" />
          </RadioButtonGroup>

          {/* Notifications */}
          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              lowContrast
              hideCloseButton={false}
              onCloseButtonClick={() => setError(null)}
            />
          )}
          {success && (
            <InlineNotification
              kind="success"
              title="Success"
              subtitle={success}
              lowContrast
              hideCloseButton={false}
              onCloseButtonClick={() => setSuccess(null)}
            />
          )}

          {/* Current shares list */}
          <div style={{ marginTop: '1rem' }}>
            <h5 style={{ marginBottom: '0.5rem' }}>Currently shared with</h5>
            {sharesLoading ? (
              <Loading small withOverlay={false} description="Loading shares..." />
            ) : shares.length === 0 ? (
              <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>
                This ontology is not shared with anyone yet.
              </p>
            ) : (
              <StructuredListWrapper isCondensed>
                <StructuredListHead>
                  <StructuredListRow head>
                    <StructuredListCell head>User</StructuredListCell>
                    <StructuredListCell head>Permission</StructuredListCell>
                    <StructuredListCell head>Actions</StructuredListCell>
                  </StructuredListRow>
                </StructuredListHead>
                <StructuredListBody>
                  {shares.map((share) => (
                    <StructuredListRow key={share.id}>
                      <StructuredListCell>
                        <span>
                          {share.shared_with_display_name || share.shared_with_email}
                        </span>
                        {share.shared_with_display_name && (
                          <span
                            style={{
                              display: 'block',
                              fontSize: '0.75rem',
                              color: 'var(--cds-text-secondary)',
                            }}
                          >
                            {share.shared_with_email}
                          </span>
                        )}
                      </StructuredListCell>
                      <StructuredListCell>
                        <Tag type="blue" size="sm">
                          {share.permission}
                        </Tag>
                      </StructuredListCell>
                      <StructuredListCell>
                        <Button
                          kind="danger--ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={TrashCan}
                          iconDescription="Revoke access"
                          onClick={() =>
                            handleRevoke(share.id, share.shared_with_email)
                          }
                        />
                      </StructuredListCell>
                    </StructuredListRow>
                  ))}
                </StructuredListBody>
              </StructuredListWrapper>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}

// Made with Bob
